import React, { useState, useEffect } from 'react';

const FilterBar = ({
  filters = [],
  activeFilters = {},
  onFilterChange,
  onClearFilters,
  searchPlaceholder = 'Ara...',
  showSearch = true
}) => {
  const hasActiveFilters = Object.values(activeFilters).some(
    value => value !== null && value !== '' && value !== 'all' && value !== undefined
  );

  const activeFilterCount = Object.values(activeFilters).filter(
    value => value !== null && value !== '' && value !== 'all' && value !== undefined
  ).length;

  const [localSearchTerm, setLocalSearchTerm] = useState(activeFilters.search || '');

  // Keep local search term in sync if activeFilters.search changes externally (e.g. cleared via badge)
  useEffect(() => {
    setLocalSearchTerm(activeFilters.search || '');
  }, [activeFilters.search]);

  const handleSearchSubmit = () => {
    onFilterChange('search', localSearchTerm);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  return (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-body p-3">
        <div className="row g-3 align-items-end">
          {/* Search Input */}
          {showSearch && (
            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
              <label className="form-label small fw-semibold text-dark mb-1">
                <i className="fas fa-search me-1 text-muted"></i>
                Arama
              </label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control bg-light border-end-0"
                  placeholder={searchPlaceholder}
                  value={localSearchTerm}
                  onChange={(e) => setLocalSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
                <button
                  className="btn btn-dark border border-start-0"
                  type="button"
                  onClick={handleSearchSubmit}
                  title="Ara"
                >
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </div>
          )}

          {/* Dynamic Filters */}
          {filters.map((filter, index) => {
            if (filter.type === 'select') {
              return (
                <div key={filter.key || index} className={filter.colClass || 'col-12 col-sm-6 col-md-4 col-lg-3'}>
                  <label className="form-label small fw-semibold text-dark mb-1">
                    {filter.label || filter.key}
                  </label>
                  <select
                    className="form-select bg-light"
                    value={activeFilters[filter.key] || filter.defaultValue || ''}
                    onChange={(e) => onFilterChange(filter.key, e.target.value)}
                  >
                    {filter.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }

            if (filter.type === 'date') {
              return (
                <div key={filter.key || index} className={filter.colClass || 'col-12 col-sm-6 col-md-4 col-lg-3'}>
                  <label className="form-label small fw-semibold text-dark mb-1">
                    {filter.label || filter.key}
                  </label>
                  <input
                    type="date"
                    className="form-control bg-light"
                    value={activeFilters[filter.key] || ''}
                    onChange={(e) => onFilterChange(filter.key, e.target.value)}
                    placeholder={filter.placeholder}
                    min={filter.min}
                    max={filter.max}
                  />
                </div>
              );
            }

            if (filter.type === 'dateRange') {
              return (
                <React.Fragment key={filter.key || index}>
                  <div className={filter.colClass || 'col-12 col-sm-6 col-md-4 col-lg-3'}>
                    <label className="form-label small fw-semibold text-dark mb-1">
                      <i className="fas fa-calendar-alt me-1 text-muted"></i>
                      {filter.startLabel || 'Başlangıç Tarihi'}
                    </label>
                    <input
                      type="date"
                      className="form-control bg-light"
                      value={activeFilters[`${filter.key}Start`] || activeFilters[`${filter.key}StartDate`] || ''}
                      onChange={(e) => onFilterChange(`${filter.key}Start` || `${filter.key}StartDate`, e.target.value)}
                      placeholder={filter.startPlaceholder}
                      max={activeFilters[`${filter.key}End`] || activeFilters[`${filter.key}EndDate`]}
                    />
                  </div>
                  <div className={filter.colClass || 'col-12 col-sm-6 col-md-4 col-lg-3'}>
                    <label className="form-label small fw-semibold text-dark mb-1">
                      <i className="fas fa-calendar-alt me-1 text-muted"></i>
                      {filter.endLabel || 'Bitiş Tarihi'}
                    </label>
                    <input
                      type="date"
                      className="form-control bg-light"
                      value={activeFilters[`${filter.key}End`] || activeFilters[`${filter.key}EndDate`] || ''}
                      onChange={(e) => onFilterChange(`${filter.key}End` || `${filter.key}EndDate`, e.target.value)}
                      placeholder={filter.endPlaceholder}
                      min={activeFilters[`${filter.key}Start`] || activeFilters[`${filter.key}StartDate`]}
                    />
                  </div>
                </React.Fragment>
              );
            }

            return null;
          })}

        </div>

        {/* Active Filter Badges */}
        {hasActiveFilters && (
          <div className="mt-3 pt-3 border-top">
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <small className="text-muted me-2 fw-semibold">Aktif Filtreler:</small>
              {Object.entries(activeFilters).map(([key, value]) => {
                if (!value || value === '' || value === 'all' || value === undefined) return null;
                
                // Key'leri Türkçe'ye çevir
                const keyTranslations = {
                  'search': 'Arama',
                  'status': 'Durum',
                  'date': 'Tarih',
                  'dateStart': 'Başlangıç Tarihi',
                  'dateEnd': 'Bitiş Tarihi',
                  'dateStartDate': 'Başlangıç Tarihi',
                  'dateEndDate': 'Bitiş Tarihi',
                  'isActive': 'Durum',
                  'subscriptionId': 'Abonelik No'
                };
                
                // dateRange için özel kontrol
                let filterLabel = '';
                let displayValue = value;
                
                if (key.endsWith('Start') || key.endsWith('StartDate')) {
                  const baseKey = key.replace('Start', '').replace('StartDate', '');
                  const filter = filters.find(f => f.key === baseKey && f.type === 'dateRange');
                  if (filter) {
                    filterLabel = filter.startLabel || 'Başlangıç Tarihi';
                    // Tarih formatını düzelt
                    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                      const date = new Date(value);
                      displayValue = date.toLocaleDateString('tr-TR');
                    }
                  } else {
                    filterLabel = keyTranslations[key] || 'Başlangıç Tarihi';
                    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                      const date = new Date(value);
                      displayValue = date.toLocaleDateString('tr-TR');
                    }
                  }
                } else if (key.endsWith('End') || key.endsWith('EndDate')) {
                  const baseKey = key.replace('End', '').replace('EndDate', '');
                  const filter = filters.find(f => f.key === baseKey && f.type === 'dateRange');
                  if (filter) {
                    filterLabel = filter.endLabel || 'Bitiş Tarihi';
                    // Tarih formatını düzelt
                    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                      const date = new Date(value);
                      displayValue = date.toLocaleDateString('tr-TR');
                    }
                  } else {
                    filterLabel = keyTranslations[key] || 'Bitiş Tarihi';
                    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
                      const date = new Date(value);
                      displayValue = date.toLocaleDateString('tr-TR');
                    }
                  }
                } else {
                const filter = filters.find(f => f.key === key);
                  filterLabel = filter?.label || keyTranslations[key] || key;
                  displayValue = filter?.options?.find(opt => opt.value === value)?.label || value;
                }
                
                // Eğer filterLabel bulunamadıysa key'i Türkçe'ye çevir
                if (!filterLabel || filterLabel === key) {
                  filterLabel = keyTranslations[key] || key;
                }
                
                return (
                  <span key={key} className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2 d-inline-flex align-items-center gap-1">
                    <span className="fw-medium">{filterLabel}:</span>
                    <span>{displayValue}</span>
                    <button
                      className="btn-close btn-close-sm ms-1"
                      onClick={() => {
                        if (key.endsWith('Start') || key.endsWith('StartDate')) {
                          onFilterChange(key, '');
                        } else if (key.endsWith('End') || key.endsWith('EndDate')) {
                          onFilterChange(key, '');
                        } else {
                          const filter = filters.find(f => f.key === key);
                          onFilterChange(key, filter?.defaultValue || '');
                        }
                      }}
                      aria-label="Kaldır"
                      style={{ fontSize: '0.65rem' }}
                    ></button>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterBar;



